import React, { useState } from "react";
import { FiBook, FiPlus, FiCalendar } from "react-icons/fi";
import { BiTrash, BiEdit } from "react-icons/bi";
import Modal from "@/components/modal";

function EducationModal({ isOpen, onClose, initialData, onSave }) {
  const [formData, setFormData] = useState(
    initialData || {
      institution: "",
      degree: "",
      field_of_study: "",
      from: "",
      to: "",
      current: false,
      description: "",
    }
  );

  React.useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        institution: "",
        degree: "",
        field_of_study: "",
        from: "",
        to: "",
        current: false,
        description: "",
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.institution || !formData.degree || !formData.from) {
      return;
    }
    onSave(formData);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-xl">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {initialData ? "Edit Education" : "Add Education"}
          </h2>
          <p className="text-sm text-gray-500 mt-1">Add your educational background</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Institution <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.institution}
              onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
              className="input w-full"
              placeholder="e.g. Harvard University"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Degree <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.degree}
              onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
              className="input w-full"
              placeholder="e.g. Bachelor's degree"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field of Study</label>
            <input
              type="text"
              value={formData.field_of_study}
              onChange={(e) => setFormData({ ...formData, field_of_study: e.target.value })}
              className="input w-full"
              placeholder="e.g. Computer Science"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="month"
                value={formData.from ? formData.from.slice(0, 7) : ""}
                onChange={(e) => setFormData({ ...formData, from: e.target.value + "-01" })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="month"
                value={formData.to ? formData.to.slice(0, 7) : ""}
                onChange={(e) => setFormData({ ...formData, to: e.target.value + "-01" })}
                className="input w-full disabled:bg-gray-100"
                disabled={formData.current}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.current}
              onChange={(e) => setFormData({ ...formData, current: e.target.checked, to: "" })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">I'm currently studying here</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full resize-y"
              rows={3}
              placeholder="Describe your studies, achievements..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary cursor-pointer"
            >
              {initialData ? "Save Changes" : "Add Education"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default function EducationSection({ values, setValues }) {
  const [showModal, setShowModal] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const education = values.education || [];

  const handleSave = (edu) => {
    if (editingEducation) {
      const index = education.findIndex((e) => 
        e._id ? e._id === editingEducation._id : e === editingEducation
      );
      const updated = [...education];
      updated[index] = edu;
      setValues({ ...values, education: updated });
    } else {
      setValues({
        ...values,
        education: [...education, edu],
      });
    }
    setEditingEducation(null);
  };

  const handleEdit = (edu) => {
    setEditingEducation(edu);
    setShowModal(true);
  };

  const handleRemove = (edu) => {
    setValues({
      ...values,
      education: education.filter((e) => e !== edu),
    });
  };

  const handleAdd = () => {
    setEditingEducation(null);
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
            <FiBook className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Education</h2>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
        >
          <FiPlus className="w-4 h-4" />
          Add
        </button>
      </div>

      <div className="p-6">
        {education.length > 0 ? (
          <div className="space-y-4">
            {education.map((edu, index) => (
              <div 
                key={edu._id || index} 
                className="group relative p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all"
              >
                <div className="flex gap-4">
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          {edu.degree}
                          {edu.field_of_study && <span className="font-normal text-gray-600"> in {edu.field_of_study}</span>}
                        </h3>
                        <p className="text-sm text-gray-600 mt-0.5">{edu.institution}</p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEdit(edu)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <BiEdit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(edu)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <BiTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Date */}
                    <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-500">
                      <FiCalendar className="w-3.5 h-3.5" />
                      <span>
                        {formatDate(edu.from)} — {edu.current ? "Present" : formatDate(edu.to) || "Present"}
                      </span>
                    </div>
                    
                    {/* Description */}
                    {edu.description && (
                      <p className="text-sm text-gray-600 mt-3 leading-relaxed">{edu.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <FiBook className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-600">No education added yet</p>
            <p className="text-sm mt-1">Add your educational background to stand out</p>
          </div>
        )}
      </div>

      <EducationModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingEducation(null);
        }}
        initialData={editingEducation}
        onSave={handleSave}
      />
    </div>
  );
}
